import { array } from '../../utils';
import { create } from 'zustand';
import { Layer, MapType, PlaceProps } from '../mapbox-common/types';

import { MapRef } from 'react-map-gl/mapbox';
import { MapTypeToStyle } from '../mapbox-common/config';

type TCenter = { logo: string; location: { lat: number; lng: number } };

interface IGoogleMapStore {
  center?: TCenter;
  centerApi?: { lat: number; lng: number };
  places: PlaceProps[];
  currentPlaces: PlaceProps[];
  currentPlaceId?: string;
  currentRoute?: any;
  selectedLayers: Layer[];
  savedLayers: Layer[];
  mapType: MapType;
  displayLayers: Layer[];
  mapRef?: MapRef;
  directionsClientRef?: any;

  actions: {
    setCenter: (center?: TCenter) => void;
    setCenterApi: (centerApi?: { lat: number; lng: number }) => void;
    setPlaces: (places: PlaceProps[]) => void;
    addPlaces: (places: PlaceProps[]) => void;
    updatePlace: (place: PlaceProps) => void;
    getPlaceById: (id: string | undefined) => PlaceProps | undefined;
    setCurrentPlaceId: (id?: string) => void;
    setCurrentRoute: (route?: any) => void;
    setSelectedLayer: (layer: Layer) => void;
    setSelectedLayers: (layers: Layer[]) => void;
    addSavedLayer: (layer: Layer, data: PlaceProps[]) => void;
    addSavedLayers: (layers: Layer[], data: PlaceProps[]) => void;
    setDisplayLayers: (displayLayers: Layer[]) => void;
    setMapType: (mapType: MapType) => void;
    setMapRef: (ref?: MapRef) => void;
    setDirectionsClientRef: (ref: any) => void;
  };
}

export const useMapboxStore = create<IGoogleMapStore>((set, get) => ({
  center: undefined,
  centerApi: undefined,
  places: [],
  currentPlaces: [],
  currentPlaceId: undefined,
  currentRoute: undefined,
  selectedLayers: [],
  savedLayers: [],
  mapType: MapType.Roadmap,
  displayLayers: [],
  mapRef: undefined,
  directionsClientRef: undefined,

  actions: {
    setCenter: (center) => set({ center }),
    setCenterApi: (centerApi) => set({ centerApi }),
    setPlaces: (places) => set({ places }),
    addPlaces: (places) =>
      set((state) => ({
        places: array.mergeWithoutDuplicates(state.places, places, 'fsq_id'),
      })),
    getPlaceById: (id) => get().places.find((p) => p.fsq_id === id),
    updatePlace: (place) =>
      set((state) => ({
        places: state.places.map((p) => (p.fsq_id === place.fsq_id ? place : p)),
      })),
    setCurrentPlaceId: (currentPlaceId) =>
      set({ currentPlaceId, currentRoute: undefined }),
    setCurrentRoute: (currentRoute) => set({ currentRoute }),
    // add/remove layer in selectedLayers
    setSelectedLayer: (layer: Layer) =>
      set((state) => {
        let selectedLayersNew: Layer[];
        if (state.selectedLayers.find((g) => g === layer)) {
          selectedLayersNew = state.selectedLayers.filter((g) => g !== layer);
        } else {
          selectedLayersNew = [layer, ...state.selectedLayers];
        }
        return {
          selectedLayers: selectedLayersNew,
          currentPlaceId: undefined,
          currentPlaces: state.places.filter((p) => selectedLayersNew.includes(p.layer)),
        };
      }),
    // set selectedLayers to specifies
    setSelectedLayers: (layers: Layer[]) =>
      set((state) => ({
        selectedLayers: layers,
        currentPlaceId: undefined,
        currentPlaces: state.places.filter((p) => layers.includes(p.layer)),
      })),
    // save layer places to reuse
    addSavedLayer: (layer: Layer, places: PlaceProps[]) =>
      set((state) => ({
        savedLayers: [...state.savedLayers, layer],
        places: array.mergeWithoutDuplicates(state.places, places, 'fsq_id'),
        currentPlaceId: undefined,
      })),
    // save layers places to reuse
    addSavedLayers: (layers: Layer[], places: PlaceProps[]) =>
      set((state) => ({
        savedLayers: [...state.savedLayers, ...layers],
        places: array.mergeWithoutDuplicates(state.places, places, 'fsq_id'),
        currentPlaceId: undefined,
      })),
    setDisplayLayers: (displayLayers: Layer[]) => set({ displayLayers }),
    setMapType: (mapType: MapType) =>
      set((state) => {
        const map = state.mapRef?.getMap();
        if (map) {
          map.setStyle(MapTypeToStyle[mapType]);
        }
        return { mapType };
      }),
    setMapRef: (ref) => set({ mapRef: ref }),
    setDirectionsClientRef: (ref) => set({ directionsClientRef: ref }),
  },
}));

export const useMapboxStoreActions = () => useMapboxStore((state) => state.actions);
